const pages = ["inbound", "dashboard", "orders", "picking", "inventory", "warehouses", "employees", "analytics", "catalog", "categories", "placement", "integration"] as const;
export type Page = typeof pages[number];
const navigationEvent = "wms:navigation";

export function readPage(hash = window.location.hash): Page | null {
  const name = hash.replace(/^#\/?/, "").split("?")[0];
  return pages.includes(name as Page) ? name as Page : null;
}

export function readRouteParams(hash = window.location.hash): URLSearchParams {
  const separator = hash.indexOf("?");
  return new URLSearchParams(separator < 0 ? "" : hash.slice(separator + 1));
}

export function resolvePage(hash: string, roles: readonly string[] = []): Page {
  const manager = roles.some(role => role === "admin" || role === "warehouse_supervisor");
  const warehouse = manager || roles.includes("picker");
  const fallback = warehouse && !manager ? "picking" : "dashboard";
  const requested = readPage(hash);
  if (!requested) return fallback;
  const warehousePage = requested === "inbound" || requested === "placement" || requested === "picking";
  return (warehousePage ? warehouse : manager) ? requested : fallback;
}

function writeRoute(page: Page, params: URLSearchParams, replace: boolean) {
  const query = params.toString();
  const hash = `#/${page}${query ? `?${query}` : ""}`;
  if (window.location.hash === hash) return;
  const url = new URL(window.location.href);
  url.hash = hash;
  window.history[replace ? "replaceState" : "pushState"](window.history.state, "", url);
  window.dispatchEvent(new Event(navigationEvent));
}

export function navigateTo(page: Page, params?: Record<string, string>, replace = false) {
  // Clicking the current menu item keeps its filters and selected tab.
  if (params === undefined && readPage() === page) return;
  writeRoute(page, new URLSearchParams(params), replace);
}

export function updateRouteParams(updates: Record<string, string | null>, replace = false) {
  const page = readPage();
  if (!page) return;
  const params = readRouteParams();
  for (const [key, value] of Object.entries(updates)) {
    if (value) params.set(key, value); else params.delete(key);
  }
  writeRoute(page, params, replace);
}

export function subscribeNavigation(listener: () => void): () => void {
  window.addEventListener("popstate", listener);
  window.addEventListener("hashchange", listener);
  window.addEventListener(navigationEvent, listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener("hashchange", listener);
    window.removeEventListener(navigationEvent, listener);
  };
}
